const EQUALWEB_SHADOW_HOST_SELECTORS = ['#INDShadowRootHost', '#INDshadowRootWrap'];

const HIDE_EW_MENU_BTN =
  '#INDmenu-btn{position:fixed!important;left:-10000px!important;top:0!important;width:1px!important;height:1px!important;opacity:0!important;overflow:hidden!important;clip:rect(0,0,0,0)!important;clip-path:inset(50%)!important;white-space:nowrap!important;border:0!important;padding:0!important;margin:0!important;}';

const EW_OFFSCREEN_STYLE_ID = 'equalweb-launch-offscreen';

const MENU_BTN_SELECTOR = '#INDmenu-btn';

type InterdealRuntime = {
  isPending?: boolean;
  menu?: HTMLElement;
  a11y?: { a11yBtn?: HTMLElement | null };
};

function getInterdeal(): InterdealRuntime | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window as { interdeal?: InterdealRuntime }).interdeal;
}

// a partir do core 5.3.1 o shadow root da EqualWeb é "closed" (host.shadowRoot vem null);
// o interdeal.menu é o #INDmenu dentro dele, então o getRootNode() devolve o próprio shadow root
export function getEqualWebShadowRoot(): ShadowRoot | null {
  const root = getInterdeal()?.menu?.getRootNode?.();
  return root instanceof ShadowRoot ? root : null;
}

function collectEqualWebShadowRoots(): ShadowRoot[] {
  const roots = EQUALWEB_SHADOW_HOST_SELECTORS.reduce<ShadowRoot[]>((acc, selector) => {
    const host = document.querySelector(selector) as
      | (HTMLElement & { shadowRoot?: ShadowRoot })
      | null;
    if (host?.shadowRoot) acc.push(host.shadowRoot);
    return acc;
  }, []);

  const closedRoot = getEqualWebShadowRoot();
  if (closedRoot && !roots.includes(closedRoot)) roots.push(closedRoot);

  return roots;
}

function findEqualWebButtonDeepFrom(root: Document | ShadowRoot): HTMLElement | null {
  const direct = root.querySelector<HTMLElement>(MENU_BTN_SELECTOR);
  if (direct) return direct;
  const tree = root.querySelectorAll<HTMLElement>('*');
  for (let i = 0; i < tree.length; i += 1) {
    const node = tree[i] as HTMLElement & { shadowRoot?: ShadowRoot | null };
    if (node.shadowRoot) {
      const found = findEqualWebButtonDeepFrom(node.shadowRoot);
      if (found) return found;
    }
  }
  return null;
}

function findEqualWebButton(): HTMLElement | null {
  const direct = document.getElementById('INDmenu-btn') as HTMLElement | null;
  if (direct) return direct;

  const inKnownHosts = collectEqualWebShadowRoots().reduce<HTMLElement | null>(
    (found, root) => found ?? root.querySelector<HTMLElement>(MENU_BTN_SELECTOR),
    null,
  );
  if (inKnownHosts) return inKnownHosts;

  return findEqualWebButtonDeepFrom(document);
}

export function installEqualWebLaunchButtonOffscreenStyle(): void {
  if (typeof document === 'undefined') return;

  if (!document.getElementById(EW_OFFSCREEN_STYLE_ID)) {
    const inDoc = document.createElement('style');
    inDoc.id = EW_OFFSCREEN_STYLE_ID;
    inDoc.textContent = HIDE_EW_MENU_BTN;
    document.head.appendChild(inDoc);
  }

  collectEqualWebShadowRoots().forEach(shadowRoot => {
    if (shadowRoot.getElementById(EW_OFFSCREEN_STYLE_ID)) return;
    const s = document.createElement('style');
    s.id = EW_OFFSCREEN_STYLE_ID;
    s.textContent = HIDE_EW_MENU_BTN;
    shadowRoot.appendChild(s);
  });
}

const RETRY_DELAYS_MS = [50, 100, 200, 400, 800, 1600, 3200, 5000];

export const showEqualWebDrawer = (): void => {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;

  let done = false;

  const run = (): void => {
    if (done) return;
    installEqualWebLaunchButtonOffscreenStyle();
    const interdeal = getInterdeal();
    // o a11yBtn é o #INDmenu-btn; clicar nele é o que o próprio core faz para abrir o menu
    // (a11y.openMenu não existe como método, só como ação de postMessage)
    const a11yBtn = interdeal?.a11y?.a11yBtn;
    if (a11yBtn) {
      if (interdeal?.isPending) {
        return;
      }
      a11yBtn.click();
      done = true;
      return;
    }
    const button = findEqualWebButton();
    if (button) {
      button.click();
      done = true;
    }
  };

  const scheduleRetries = (): void => {
    RETRY_DELAYS_MS.forEach(ms => {
      window.setTimeout(() => {
        if (!done) run();
      }, ms);
    });
  };

  run();
  if (done) return;

  const onAccLoaded = () => {
    if (!done) run();
  };
  document.addEventListener('INDAccLoaded', onAccLoaded, { once: true, capture: true });

  scheduleRetries();
};
